/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Alert, Pagination, Snackbar } from "@mui/material";
import { acceptPendingRecord, rejectPendingRecord } from "apis/visitors/record";
import { TVisit } from "apis/visitors/type";
import { useEffect, useState } from "react";
import { theme } from "styles/theme";
import Button from "uis/Button";
import AlertModal from "uis/modal/alert-modal";
import { getKorDate, getKorTime } from "utils/date";
import LinkCalendarModal from "./link-calendar-modal";
import Icon from "uis/Icon";
import { getPersonalCalendarConnection } from "apis/personal-calendar";

const Annotation = styled.p`
  color: ${theme.neutral500};
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 11px;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px;
  width: 100%;
  box-sizing: border-box;
`;

const TableHeader = styled.div`
  display: flex;
  border-radius: 5px;
  background-color: ${theme.neutral50};
  padding: 16px 24px;
  gap: 10px;
`;

const HeaderLabel = styled.p<{ width?: number }>`
  color: ${theme.neutral900};
  font-size: 16px;
  font-weight: 700;
  width: ${(props) => props.width + "px"};
`;

const Label = styled.p<{ width?: number }>`
  color: ${theme.neutral600};
  font-size: 16px;
  font-weight: 500;
  ${(props) => (props.width ? `width: ${props.width}px;` : null)}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;
  margin-right: 24px;
`;

const PaginationContainer = styled.div`
  margin: 24px 0 24px auto;
`;

const VisitPending = ({
  records,
  onChange,
  page,
  totalPage,
}: {
  records: TVisit[];
  onChange?: (page: number) => void;
  page?: number;
  totalPage?: number;
}) => {
  const [reject, setReject] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [rejectId, setRejectId] = useState<string>();
  const [acceptId, setAcceptId] = useState<string>();
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);

  const onHandleReject = async () => {
    if (rejectId && page) {
      const res = await rejectPendingRecord(rejectId);
      if (res) {
        onChange?.(page);
        setReject(true);
        setOpenAlert(false);
      }
    }
  };

  const onHandleAccept = async (id?: string) => {
    if (id && page) {
      const res = await acceptPendingRecord(id);
      if (res.success) {
        setAcceptId(id);
        onChange?.(page);
        setSuccess(true);
        getPersonalCalendarConnection().then((res) => {
          setOpenCalendar(res.success);
        });
      }
    }
  };

  return (
    <Root>
      <Snackbar
        open={reject}
        autoHideDuration={3000}
        onClose={() => setReject(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success">방문 요청을 거절하였습니다.</Alert>
      </Snackbar>
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success">방문 요청을 승인하였습니다.</Alert>
      </Snackbar>
      <Annotation>* 방문 날짜가 지난 방문 요청은 자동으로 거절됩니다.</Annotation>
      <TableHeader>
        <HeaderLabel width={20}></HeaderLabel>
        <HeaderLabel width={150}>방문 일자</HeaderLabel>
        <HeaderLabel width={150}>방문 시간</HeaderLabel>
        <HeaderLabel width={150}>방문자</HeaderLabel>
        <HeaderLabel width={150}>방문자 소속</HeaderLabel>
      </TableHeader>
      {records.length === 0 && (
        <Label
          css={css`
            padding: 24px;
          `}
        >
          방문 요청 내역이 없습니다.
        </Label>
      )}
      {records.map((item, index) => {
        return (
          <TableRow
            record={item}
            key={index}
            onReject={() => {
              setOpenAlert(true);
              setRejectId(item.id);
            }}
            onAccept={() => {
              onHandleAccept(item.id);
            }}
          />
        );
      })}
      <AlertModal
        closeModal={() => setOpenAlert(false)}
        modalIsOpen={openAlert}
        title={"일정 요청을 거절하시겠습니까?"}
        onChange={onHandleReject}
        btnTitle="거절하기"
        content={
          <p
            css={css`
              font-size: 14px;
              font-weight: 500;
              text-align: center;
            `}
          >
            거절하신 요청은 다시 복구할 수 없습니다.
          </p>
        }
      />
      <PaginationContainer>
        <Pagination
          page={page}
          count={totalPage}
          onChange={async (e, page) => {
            onChange?.(page);
          }}
        />
      </PaginationContainer>
      {acceptId && (
        <LinkCalendarModal closeModal={() => setOpenCalendar(false)} modalIsOpen={openCalendar} id={acceptId} />
      )}
    </Root>
  );
};

export default VisitPending;

const TableRowContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 5px;
  border-bottom: 2px solid ${theme.neutral50};
  padding: 0 0 0 24px;

  &:hover {
    background-color: ${theme.neutral50};
  }
`;

const TableRowLabel = styled.p<{ width?: number }>`
  color: ${theme.neutral600};
  font-size: 16px;
  font-weight: 500;
  width: ${(props) => props.width + "px"};
`;

const SectionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 20px 48px;
  gap: 12px;
  border-bottom: 2px solid ${theme.neutral50};
`;

const DetailTitle = styled.p`
  color: ${theme.neutral600};
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
`;

const DetailLabel = styled.p`
  color: ${theme.neutral600};
  font-size: 16px;
  font-weight: 500;
  line-height: 140%;
`;

const DetailContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const TableRow = ({ record, onReject, onAccept }: { record: TVisit; onReject?: () => void; onAccept?: () => void }) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <TableRowContainer>
        <div
          onClick={() => setOpen(!open)}
          css={css`
            display: flex;
            padding: 8px 0;
            align-items: center;
            height: 100%;
            gap: 10px;
          `}
        >
          <TableRowLabel
            width={20}
            css={css`
              height: 20px;
              cursor: pointer;
              transform: rotate(${open ? "180deg" : "0deg"});
              transition: 0.3s ease all;
            `}
          >
            <Icon name="top-arrow" width={20} />
          </TableRowLabel>
          <TableRowLabel width={150}>{getKorDate(record.visitDatetime)}</TableRowLabel>
          <TableRowLabel width={150}>{getKorTime(record.visitDatetime)}</TableRowLabel>
          <TableRowLabel width={150}>
            {record.visitors[0].name}
            {record.visitors.length > 1 && `외 ${record.visitors.length - 1}명`}
          </TableRowLabel>
          <TableRowLabel width={150}>{record.visitorDepartment}</TableRowLabel>
        </div>
        <Button
          title="승인하기"
          type="secondary"
          onClick={onAccept}
          styles={{
            container: css`
              padding: 10px 14px;
              margin-left: auto;
              margin: 8px 8px 8px auto;
            `,
          }}
        />
        <Button
          onClick={onReject}
          title="거절하기"
          type="destructive"
          styles={{
            container: css`
              padding: 10px 14px;
            `,
          }}
        />
      </TableRowContainer>
      {open && (
        <SectionContainer>
          <DetailContainer>
            <DetailTitle>방문자 | </DetailTitle>
            <DetailLabel>{record.visitors.map((item) => item.name).join(", ")}</DetailLabel>
          </DetailContainer>
          <DetailContainer>
            <DetailTitle>방문자 소속 | </DetailTitle>
            <DetailLabel>{record.visitorDepartment}</DetailLabel>
          </DetailContainer>
        </SectionContainer>
      )}
    </>
  );
};
